import {
  Box, Button, Pagination, Tooltip, Typography
} from '@mui/material'
import { differenceInCalendarDays, format, isValid } from 'date-fns'
import { de } from 'date-fns/locale'
import { TFunction } from 'i18next'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { NextInspectionDto } from '../../../api/types/checklist'
import { scheduleRepeatOptions } from '../../../constants'
import { TOKENS } from '../../../pages/dashboard-v2/components/primitives'
import { TitleCell } from './folders/NewDesignCells'
import LoadSpinner from '../../loadSpinner/LoadSpinner'

// "Geplant" in the new design (design draft screen 6): template, repeat in words, next start,
// last run. Data, sorting, paging and the start link come from ScheduledChecklistsTable.

interface Props {
  items: NextInspectionDto[]
  isLoading: boolean
  isPhone: boolean
  startLink: (x: NextInspectionDto) => string
  onLatest: (scheduleId: number) => void
  // click on a title: show the schedule of the planned process
  onOpen: (x: NextInspectionDto) => void
  sortColumn: string
  sortDir: string
  onSort: (field: string, direction: 'asc' | 'desc') => void
  page: number
  totalPages: number
  onPagination: (page: number) => void
  sentinel?: ReactNode
}

function nextStart(value: Date | string | null | undefined, t: TFunction) {
  if (!value) return { label: '', abs: '' }
  const date = new Date(value as any)
  if (!isValid(date)) return { label: '', abs: '' }
  const days = differenceInCalendarDays(date, new Date())
  const time = format(date, 'HH:mm')
  let label = format(date, 'dd.MM.yyyy')
  if (days === 0) label = t('processList.today', { time })
  else if (days === 1) label = t('processList.tomorrow', { time })
  else if (days > 1 && days < 7) label = `${format(date, 'EEEE', { locale: de })} ${time}`
  return { label, abs: format(date, 'dd.MM.yyyy HH:mm') }
}

export default function ScheduledListV2(props: Props) {
  const {
    items, isLoading, isPhone, startLink, onLatest, onOpen, sortColumn, sortDir, onSort, page, totalPages, onPagination, sentinel
  } = props
  const { t } = useTranslation()
  const sortBy = (field: string) => onSort(field, sortColumn === field && sortDir === 'asc' ? 'desc' : 'asc')
  const mark = (field: string) => (sortColumn === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '')
  const grid = 'minmax(0,1fr) 200px 190px 170px 150px'

  const rows = items.map((x) => {
    const repeat = scheduleRepeatOptions.find((o) => o.value === x.repeat)
    const start = nextStart(x.start_date ?? x.due_date, t)
    const sub = [x.project_name, x.template_title !== x.title ? x.template_title : null].filter(Boolean).join(' · ')
    const notes = (
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
        {x.autostart && <Chip tone="warning">{t('overview.startsAutomatically')}</Chip>}
        {x.rescheduled_due_to_non_working_day && (
          <Tooltip title={t('schedule.rescheduledTooltip')}><span><Chip tone="warning">{t('scheduleList.rescheduled')}</Chip></span></Tooltip>
        )}
        {x.non_working_day_excluded && (
          <Tooltip title={t('schedule.nonWorkingDayTooltip')}><span><Chip>{t('scheduleList.skipped')}</Chip></span></Tooltip>
        )}
      </Box>
    )
    const canStart = !x.autostart && !x.non_working_day_excluded
    const startButton = canStart ? (
      <Button
        component={Link}
        to={startLink(x)}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap' }}
      >
        {t('scheduleList.startNow')}
      </Button>
    ) : null
    const latest = (
      <Button
        size="small"
        onClick={() => onLatest(x.schedule_id)}
        sx={{ textTransform: 'none', fontWeight: 600, px: 0, minWidth: 0 }}
      >
        {t('scheduleList.latest')}
      </Button>
    )

    if (isPhone) {
      return (
        <Box
          key={`${x.schedule_id}-${x.due_date}`}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 1, p: 1.75, bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px'
          }}
        >
          <TitleCell title={x.title} to={canStart ? startLink(x) : '#'} sub={sub} onOpen={() => onOpen(x)} />
          <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>
            {repeat ? t(repeat.translationKey) : ''}
            {start.label ? ` · ${t('scheduleList.next')} ${start.label}` : ''}
          </Typography>
          {notes}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>{startButton}{latest}</Box>
        </Box>
      )
    }

    return (
      <Box
        key={`${x.schedule_id}-${x.due_date}`}
        sx={{
          display: 'grid', gridTemplateColumns: grid, gap: 2, alignItems: 'center', px: 2.5, py: 1.5, borderTop: `1px solid ${TOKENS.divider}`, bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F7F9FC' }
        }}
      >
        <Box sx={{ minWidth: 0, textDecoration: x.non_working_day_excluded ? 'line-through' : 'none' }}>
          <TitleCell title={x.title} to={canStart ? startLink(x) : '#'} sub={sub} onOpen={() => onOpen(x)} />
          {notes}
        </Box>
        <Typography sx={{ fontSize: 13.5 }}>{repeat ? t(repeat.translationKey) : ''}</Typography>
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{start.label}</Typography>
          <Typography sx={{ fontSize: 12, color: TOKENS.muted }}>{start.abs}</Typography>
        </Box>
        <Box>{latest}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{startButton}</Box>
      </Box>
    )
  })

  // first load: spinner instead of an empty table
  if (isLoading && items.length === 0) {
    return <Box sx={{ py: 6 }}><LoadSpinner className="centered" /></Box>
  }

  return (
    // reloading (sort, page): keep the rows, dimmed
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1.5, opacity: isLoading ? 0.55 : 1, transition: 'opacity .15s'
    }}
    >
      {isPhone ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{rows}</Box>
      ) : (
        <Box sx={{
          bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px', overflow: 'hidden', boxShadow: TOKENS.cardShadow
        }}
        >
          <Box sx={{
            display: 'grid', gridTemplateColumns: grid, gap: 2, px: 2.5, py: 1.25, bgcolor: '#F7F9FC', fontSize: 12, fontWeight: 600, color: TOKENS.muted
          }}
          >
            <Box component="button" type="button" onClick={() => sortBy('title')} sx={headerButton}>{t('scheduleList.template')}{mark('title')}</Box>
            <Box component="button" type="button" onClick={() => sortBy('repeat')} sx={headerButton}>{t('scheduleList.repeat')}{mark('repeat')}</Box>
            <Box component="button" type="button" onClick={() => sortBy('start_date')} sx={headerButton}>{t('scheduleList.nextStart')}{mark('start_date')}</Box>
            <span>{t('scheduleList.lastRun')}</span>
            <span />
          </Box>
          {rows}
        </Box>
      )}
      {!isLoading && items.length === 0 && (
        <Typography sx={{ color: TOKENS.muted, fontSize: 14, py: 3, textAlign: 'center' }}>{t('datatable.noData')}</Typography>
      )}
      {!isPhone && totalPages > 1 && (
        <Pagination page={page} count={totalPages} onChange={(_, value) => onPagination(value)} shape="rounded" />
      )}
      {sentinel}
    </Box>
  )
}

function Chip({ children, tone = 'neutral' }: { children: ReactNode, tone?: 'neutral' | 'warning' }) {
  const colors = tone === 'warning' ? { bg: '#FFF4E0', fg: '#8A5300' } : { bg: '#F1F3F6', fg: TOKENS.muted }
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '999px', fontSize: 12, fontWeight: 600, bgcolor: colors.bg, color: colors.fg, whiteSpace: 'nowrap'
      }}
    >
      {children}
    </Box>
  )
}

const headerButton = {
  all: 'unset',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.muted,
  '&:hover': { color: TOKENS.text }
} as const
